import { PlainButton, Popover } from "@jugl-web/ui-components/cross-platform";
import { cx, useTranslations } from "@jugl-web/utils";
import { useTheme } from "@web-src/themes/useTheme";
import { ReactComponent as BlueThemeIcon } from "./assets/blue-theme.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/chevron-right.svg";
import { ReactComponent as DarkThemeIcon } from "./assets/dark-theme.svg";
import { ReactComponent as LightThemeIcon } from "./assets/light-theme.svg";
import { ReactComponent as ThemeIcon } from "./assets/theme-icon.svg";

interface ThemeOptionProps {
  icon: React.ReactNode;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

const ThemeOption = ({
  icon,
  label,
  isSelected,
  onClick,
}: ThemeOptionProps) => (
  <PlainButton
    className={cx(
      "flex w-full items-center gap-3 rounded-xl p-3 transition-colors",
      isSelected ? "bg-primary-50 hover:bg-[#D2ECFF]" : "hover:bg-grey-200"
    )}
    onClick={onClick}
  >
    <span className="shrink-0">{icon}</span>
    <span className="text-dark font-secondary text-sm font-medium">
      {label}
    </span>
  </PlainButton>
);

export const ThemePicker = () => {
  const { t } = useTranslations();
  const { customTheme, setCustomTheme } = useTheme();

  const themeLabels = {
    light: t({
      id: "main-navbar-component.light",
      defaultMessage: "Light",
    }),
    blue: t({
      id: "main-navbar-component.blue",
      defaultMessage: "Blue",
    }),
    dark: t({
      id: "main-navbar-component.dark",
      defaultMessage: "Dark",
    }),
  };

  return (
    <Popover
      placement="right-start"
      className=" -ml-4 w-[10rem] p-3"
      renderTrigger={({ Trigger, triggerRef, isOpen: isThemeOpen }) => (
        <Trigger
          ref={triggerRef}
          as={PlainButton}
          className={cx(
            "flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3.5 transition-colors",
            isThemeOpen ? "bg-grey-100" : "hover:bg-grey-100"
          )}
        >
          <span className="shrink-0">
            <ThemeIcon />
          </span>
          <span className="text-dark font-secondary grow text-left text-sm">
            {t(
              {
                id: "profile-popover.theme",
                defaultMessage: "Theme: {theme}",
              },
              { theme: themeLabels[customTheme] }
            )}
          </span>
          <ChevronRightIcon
            className={cx(
              "transition-transform",
              isThemeOpen ? "-rotate-90" : "rotate-90"
            )}
          />
        </Trigger>
      )}
    >
      {({ onClose: onCloseTheme }) => (
        <div className="flex flex-col gap-1">
          <ThemeOption
            icon={
              <div className="bg-primary-50 flex h-8 w-8 items-center justify-center rounded-md border border-solid border-white">
                <LightThemeIcon className="fill-primary h-[19.2px] w-[19.2px]" />
              </div>
            }
            label={themeLabels.light}
            isSelected={customTheme === "light"}
            onClick={() => {
              setCustomTheme("light");
              onCloseTheme();
            }}
          />
          <ThemeOption
            icon={
              <div className="bg-primary flex h-8 w-8 items-center justify-center rounded-md border border-solid border-transparent">
                <BlueThemeIcon className="h-[19.2px] w-[19.2px]" />
              </div>
            }
            label={themeLabels.blue}
            isSelected={customTheme === "blue"}
            onClick={() => {
              setCustomTheme("blue");
              onCloseTheme();
            }}
          />
          <ThemeOption
            icon={
              <div className="flex h-8 w-8 items-center justify-center rounded-md border border-solid border-transparent bg-[#333333]">
                <DarkThemeIcon />
              </div>
            }
            label={themeLabels.dark}
            isSelected={customTheme === "dark"}
            onClick={() => {
              setCustomTheme("dark");
              onCloseTheme();
            }}
          />
        </div>
      )}
    </Popover>
  );
};
